import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { ChevronRight, ExternalLink, Eye } from 'lucide-react';
import { ADMIN_PAGES_KEYS, NS } from '../../../../i18n/constants/keys';

export interface JobOpeningsHeaderProps {
  totalRecords: number;
  isClosed?: boolean;
}

export const JobOpeningsHeader = ({ totalRecords, isClosed = false }: JobOpeningsHeaderProps) => {
  const { t } = useTranslation(NS.adminPages);

  return (
    <Box sx={{ mb: 2 }}>
      {/* Top Breadcrumb & Link row */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 1.5,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            component={Link}
            to="#"
            sx={{
              color: 'text.secondary',
              textDecoration: 'none',
              fontSize: '0.75rem',
              fontWeight: 500,
              transition: 'color 0.15s ease',
              '&:hover': { color: 'primary.main' },
            }}
          >
            {t(ADMIN_PAGES_KEYS.jobOpenings.list.breadcrumbParent)}
          </Box>
          <ChevronRight size={14} color="#94a3b8" />
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.75rem' }}
          >
            {t(ADMIN_PAGES_KEYS.jobOpenings.list.breadcrumbCurrent)}
          </Typography>
        </Stack>

        <Box
          component={Link}
          to="/jobs"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            fontSize: '0.75rem',
            fontWeight: 600,
            textDecoration: 'none',
            color: 'text.secondary',
            px: 1.25,
            py: 0.5,
            borderRadius: 1.5,
            border: 1,
            borderColor: 'grey.200',
            bgcolor: 'background.paper',
            transition: 'all 0.15s ease',
            '&:hover': {
              color: 'primary.main',
              borderColor: 'primary.main',
              bgcolor: 'primary.50',
              '& .external-link-icon': { transform: 'translateX(2px)' },
            },
          }}
        >
          <Eye size={13} />
          <span>Public Preview</span>
          <ExternalLink size={13} className="external-link-icon" style={{ transition: "transform 0.2s ease" }} />
        </Box>
      </Box>

      {/* Main Title Banner */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pt: 1.5,
          pb: 0,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.02em' }}>
            {t(ADMIN_PAGES_KEYS.jobOpenings.list.breadcrumbCurrent)}
          </Typography>
          <Chip
            label={`${totalRecords} ${totalRecords === 1 ? 'Opening' : 'Openings'}`}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: '0.75rem',
              bgcolor: isClosed ? 'error.light' : 'emerald.50',
              color: isClosed ? 'error.main' : 'success.dark',
              border: '1px solid',
              borderColor: isClosed ? 'error.light' : 'success.light',
            }}
          />
        </Stack>
      </Box>
    </Box>
  );
};
